import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../styles.css';
import 'flatpickr/dist/flatpickr.min.css';
import Flatpickr from 'react-flatpickr';
import Sidebar from './Sidebar';
import StatCard from './StatCard';
import ActivityTable from './ActivityTable';
import SearchableUserSelect from './SearchableUserSelect';
import PageLoader, { LoaderButtonLabel } from './PageLoader';
import { useNavigate } from "react-router-dom";
import { Session } from "../components/utils/session";
import { API_BASE_URL, SHOW_TEAMS_FILTER } from '../config';
import { toInputDate } from '../utils/dateOnly';
import { getDefaultReportDateRange } from '../utils/loginTime';

const { startDate: defaultStartDate, endDate: defaultEndDate } = getDefaultReportDateRange(6);
const REPORT_FILTERS_KEY = 'epa.productivityReportFilters';

const baseDefaultFilters = {
  workspace: '',
  employee: '',
  startDate: defaultStartDate,
  endDate: defaultEndDate,
  mode: 'weekly',
};

function loadSavedReportFilters(fallback) {
  try {
    const raw = sessionStorage.getItem(REPORT_FILTERS_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return fallback;
    return {
      workspace: String(parsed.workspace ?? ''),
      employee: String(parsed.employee ?? ''),
      startDate: parsed.startDate || fallback.startDate,
      endDate: parsed.endDate || fallback.endDate,
      mode: parsed.mode === 'daily' ? 'daily' : 'weekly',
    };
  } catch {
    return fallback;
  }
}

function saveReportFilters(filters) {
  try {
    sessionStorage.setItem(REPORT_FILTERS_KEY, JSON.stringify(filters));
  } catch {
    /* ignore quota / private mode */
  }
}

export default function DashboardContent() {
  const role = Session.getRole();
  const token = Session.getToken();
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);
  const [summary, setSummary] = useState({ productivityPercent: 0, neutralPercent: 0, });
  const [summaryError, setSummaryError] = useState(null);
  const [teams, setTeams] = useState([]);

  const defaultFilters = baseDefaultFilters;
  const [filterDraft, setFilterDraft] = useState(() => loadSavedReportFilters(defaultFilters));
  const [appliedFilters, setAppliedFilters] = useState(() => loadSavedReportFilters(defaultFilters));
  const [refreshKey, setRefreshKey] = useState(0);
  const [dataLoading, setDataLoading] = useState(true);
  const filterDraftRef = useRef(loadSavedReportFilters(defaultFilters));

  const syncFilterDraft = useCallback((next) => {
    filterDraftRef.current = next;
    setFilterDraft(next);
  }, []);

  useEffect(() => {
    saveReportFilters(appliedFilters);
  }, [appliedFilters]);

  const handleSearch = () => {
    const next = { ...filterDraftRef.current };
    syncFilterDraft(next);
    setAppliedFilters(next);
    setRefreshKey((k) => k + 1);
  };

  /*const handleModeChange = (mode) => {
    const next = { ...filterDraftRef.current, mode };
    syncFilterDraft(next);
    setAppliedFilters(next);
    setRefreshKey((k) => k + 1);
  };*/
  const handleModeChange = (mode) => {
    let next = { ...filterDraftRef.current, mode };
  
    if (mode === "daily") {
      const today = toInputDate(new Date());
  
      next = {
        ...next,
        startDate: today,
        endDate: today,
      };
    } else {
      const { startDate, endDate } = getDefaultReportDateRange(6);
  
      next = {
        ...next,
        startDate,
        endDate,
      };
    }
  
    syncFilterDraft(next);
    setAppliedFilters(next);
    setRefreshKey((k) => k + 1);
  };

  const handleClearFilter = () => {
    filterDraftRef.current = defaultFilters;
    setFilterDraft(defaultFilters);
    setAppliedFilters(defaultFilters);
    setRefreshKey((k) => k + 1);
  };

  const parseReportTimeSeconds = (str) => {
    if (!str) return 0;
    const cleanStr = String(str).replace(/^-/, '');
    const parts = cleanStr.split(':');
    if (parts.length !== 3) return 0;
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const seconds = parseInt(parts[2], 10) || 0;
    return hours * 3600 + minutes * 60 + seconds;
  };

  const handleReportRows = useCallback((rows) => {
    let totalProductive = 0;
    let totalNeutral = 0;

    (rows || []).forEach((r) => {
      totalProductive += parseReportTimeSeconds(r.productive ?? r.Productive);
      totalNeutral += parseReportTimeSeconds(r.neutral ?? r.Neutral);
    });

    const total = totalProductive + totalNeutral;
    if (total <= 0) {
      setSummary({ productivityPercent: 0, neutralPercent: 0 });
      return;
    }

    setSummary({
      productivityPercent: Math.round((totalProductive / total) * 100),
      neutralPercent: Math.round((totalNeutral / total) * 100),
    });
  }, []);

  const handleDateRangeChange = (selectedDates) => {
    if (!selectedDates.length) {
      syncFilterDraft({ ...filterDraftRef.current, startDate: '', endDate: '' });
      return;
    }

    const start = toInputDate(selectedDates[0]);
    if (selectedDates.length === 1) {
      syncFilterDraft({ ...filterDraftRef.current, startDate: start, endDate: '' });
      return;
    }

    const end = toInputDate(selectedDates[1]);
    syncFilterDraft({ ...filterDraftRef.current, startDate: start, endDate: end });
  };

  const dateRangeValue = filterDraft.startDate && filterDraft.endDate
    ? [filterDraft.startDate, filterDraft.endDate]
    : filterDraft.startDate
      ? [filterDraft.startDate]
      : [];

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    const fetchTeams = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/Workspace/Workspaces_List`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const data = await res.json();
        const list = (data || []).map((w) => ({
          id: String(w.WorkspaceID ?? w.workspaceID ?? w.workspaceId ?? w.id ?? ''),
          name: w.WorkspaceName ?? w.workspaceName ?? w.name ?? '',
        })).filter((w) => w.id);
        setTeams(list);
      } catch (err) {
        if (err.name === 'AbortError') return;
        setTeams([]);
      }
    };
    fetchTeams();
    return () => controller.abort();
  }, [token]);

  const navigate = useNavigate();

  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.style.display = showMobileSidebar ? 'block' : 'none';
    }
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      if (showMobileSidebar) sidebar.classList.add('show-mobile'); else sidebar.classList.remove('show-mobile');
      if (hideDesktop) sidebar.classList.add('hide-desktop'); else sidebar.classList.remove('hide-desktop');
    }
    const content = document.getElementById('content');
    if (content) {
      if (hideDesktop) content.classList.add('full-width'); else content.classList.remove('full-width');
    }
  }, [showMobileSidebar, hideDesktop]);

  const toggleSidebar = () => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar(s => !s);
    } else {
      setHideDesktop(s => !s);
    }
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) {
        setShowMobileSidebar(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const prod = Math.round(summary.productivityPercent || 0);
const neutral = Math.round(summary.neutralPercent || 0);

  const statCards = [
    {
      percent: prod,
      chartColor: '#0084fe',
      value: `${prod}%`,
      label: 'Members Productivity Time',
    },
    {
      percent: neutral,
      chartColor: '#fcc419',
      value: `${neutral}%`,
      label: 'Members Neutral Time',
    },
  ];

  return (
    <div>
      <div id="sidebar-overlay" className="sidebar-overlay" onClick={() => setShowMobileSidebar(false)} />

<Sidebar />
      <div id="content">
      
       <div className='d-flex align-items-center mb-0'>
       <div className="d-flex align-items-center">
          <button type="button" id="sidebarCollapse" className="btn btn-toggle me-3" onClick={toggleSidebar}>
            <i className="fas fa-bars" />
          </button>
        </div>
       <h2 className='mt-2'>Productivity Reports</h2>     
       </div>
       <div className="row g-3 mb-4 filter-row align-items-end">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="filter-box">
              <input
                type="radio"
                className="btn-check"
                id="daily"
                checked={filterDraft.mode === 'daily'}
                onChange={() => handleModeChange('daily')}
              />
              <label className="btn btn-outline-custom px-3" htmlFor="daily">
                Daily
              </label>
              <input
                type="radio"
                className="btn-check"
                id="weekly"
                checked={filterDraft.mode === 'weekly'}
                onChange={() => handleModeChange('weekly')}
              />
              <label className="btn btn-outline-custom px-3" htmlFor="weekly">
                Weekly
              </label>
            </div>
          </div>
          {SHOW_TEAMS_FILTER && (
          <div className="col-6 col-md-6 col-xl-3">
            <select
              className="form-select reportsInput"
              value={filterDraft.workspace}
              onChange={(e) => syncFilterDraft({ ...filterDraftRef.current, workspace: e.target.value })}
            >
              <option value="">All Teams</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          )}
          <div className="col-6 col-md-6 col-xl-3">
            <SearchableUserSelect
              value={filterDraft.employee}
              onChange={(userName) => syncFilterDraft({ ...filterDraftRef.current, employee: userName })}
              onDraftChange={(userName) => {
                filterDraftRef.current = { ...filterDraftRef.current, employee: userName };
              }}
              placeholder="Search username"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div className="col-6 col-md-6 col-xl-3">
            <label className="small text-muted d-block mb-1">Date range</label>
            <Flatpickr
              className="form-control reportsInput"
              options={{
                mode: 'range',
                dateFormat: 'Y-m-d',
                altInput: true,
                altFormat: 'd-m-Y',
                rangeSeparator: ' to ',
                maxDate: 'today',
                closeOnSelect: false,
              }}
              value={dateRangeValue}
              onChange={handleDateRangeChange}
              placeholder="Start date, then end date"
            />
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary flex-grow-1"
                onClick={handleSearch}
                disabled={dataLoading}
              >
                <LoaderButtonLabel loading={dataLoading} loadingLabel="Searching...">
                  Search
                </LoaderButtonLabel>
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary flex-grow-1"
                onClick={handleClearFilter}
                disabled={dataLoading}
              >
                <LoaderButtonLabel loading={dataLoading} loadingLabel="Clearing...">
                  Clear Filter
                </LoaderButtonLabel>
              </button>
            </div>
          </div>
        </div>
        <div className="epa-page-loader-host mb-4">
          {dataLoading && (
            <PageLoader label="Loading summary..." compact overlay />
          )}
          <div className={`row g-3 ${dataLoading ? 'epa-content-dimmed' : ''}`}>
          {statCards.map(card => (
            <div className="col-12 col-lg-6 col-xl-6" key={card.label}>
              <StatCard {...card} />
            </div>
          ))}
          </div>
        </div>
        <div className="main-card">
          <ActivityTable
            mode={appliedFilters.mode}
            WorkspaceID={appliedFilters.workspace}
            employeeName={appliedFilters.employee}
            startDate={appliedFilters.startDate}
            endDate={appliedFilters.endDate}
            refreshKey={refreshKey}
            onReportRows={handleReportRows}
            onLoadingChange={setDataLoading}
          />
        </div>
      </div>
    </div>
  );
}
